/**
 * Admin operating-market registry (Layer 2; m0101). The operator registers the cities/markets the platform runs
 * as **DB data** (owner directive: "nada era para ser em hardcode"): a typed `cc:slug` market with a country,
 * optional administrative region, display name, IANA timezone, and optional WGS84 center — born `DRAFT`, advanced
 * through its lifecycle from the list. The form validation is pure + framework-neutral (tested in Node); the
 * screen renders the issues and the server re-validates every invariant. Neutral: nothing is pre-filled with a
 * country.
 */
import { type Result, ok, err } from '@vello/core';
import {
  type AdministrativeRegionView,
  type BannerView,
  type BroadcastCampaignView,
  type MarketsApi,
  type OperatingMarketView,
  type RegisterMarketInput,
  type MarketLaunchStatus,
  type MarketStatusReason,
  type MarketStatusChangeView,
  type ApiError,
} from '@vello/api-client';

// ---- register-a-market form ----------------------------------------------------------------------

/** The market-registration form's raw fields as the operator types them. */
export interface MarketForm {
  readonly countryCode: string;
  /** The market slug (`[a-z0-9-]`), e.g. `paracatu`; combined with the country into the `cc:slug` id server-side. */
  readonly slug: string;
  readonly name: string;
  /** IANA timezone, e.g. `America/Sao_Paulo`. */
  readonly timezone: string;
  /** ISO 3166-2 administrative region, when applicable, e.g. `BR-MG`. Optional. */
  readonly regionCode: string;
  /** WGS84 latitude, when known. Optional; paired with longitude. */
  readonly latitude: string;
  /** WGS84 longitude, when known. Optional; paired with latitude. */
  readonly longitude: string;
}

export type MarketFormField = 'countryCode' | 'slug' | 'name' | 'timezone' | 'regionCode' | 'latitude' | 'longitude';

export interface MarketFormIssue {
  readonly field: MarketFormField;
  readonly code:
    | 'INVALID_COUNTRY'
    | 'INVALID_SLUG'
    | 'INVALID_NAME'
    | 'INVALID_TIMEZONE'
    | 'INVALID_REGION'
    | 'REGION_COUNTRY_MISMATCH'
    | 'INVALID_COORD'
    | 'COORD_PAIR';
}

export function emptyMarketForm(): MarketForm {
  return { countryCode: '', slug: '', name: '', timezone: '', regionCode: '', latitude: '', longitude: '' };
}

const CC_ALPHA2 = /^[A-Z]{2}$/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const REGION = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;

/** Whether a string is a valid IANA timezone (the same test the `@vello/config` kernel applies server-side). */
function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const finiteInRange = (raw: string, lo: number, hi: number): number | undefined => {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isFinite(n) && n >= lo && n <= hi ? n : undefined;
};

/**
 * Validate + normalize the form into the wire `RegisterMarketInput`, or return every field issue at once. Pure and
 * framework-neutral. `countryCode` upper-cases; `slug` lower-cases; a region must match the country; a center is
 * all-or-nothing and within WGS84 bounds.
 */
export function normalizeMarketForm(form: MarketForm): Result<RegisterMarketInput, readonly MarketFormIssue[]> {
  const issues: MarketFormIssue[] = [];
  const cc = form.countryCode.trim().toUpperCase();
  if (!CC_ALPHA2.test(cc)) issues.push({ field: 'countryCode', code: 'INVALID_COUNTRY' });
  const slug = form.slug.trim().toLowerCase();
  if (!SLUG.test(slug)) issues.push({ field: 'slug', code: 'INVALID_SLUG' });
  const name = form.name.trim();
  if (name === '') issues.push({ field: 'name', code: 'INVALID_NAME' });
  const timezone = form.timezone.trim();
  if (timezone === '' || !isValidTimeZone(timezone)) issues.push({ field: 'timezone', code: 'INVALID_TIMEZONE' });
  const region = form.regionCode.trim().toUpperCase();
  if (region !== '') {
    if (!REGION.test(region)) issues.push({ field: 'regionCode', code: 'INVALID_REGION' });
    else if (CC_ALPHA2.test(cc) && !region.startsWith(`${cc}-`)) issues.push({ field: 'regionCode', code: 'REGION_COUNTRY_MISMATCH' });
  }
  // Center is all-or-nothing; each coordinate finite + within WGS84 bounds.
  const latRaw = form.latitude.trim();
  const lngRaw = form.longitude.trim();
  let center: { latitude: number; longitude: number } | undefined;
  if (latRaw !== '' || lngRaw !== '') {
    if (latRaw === '' || lngRaw === '') {
      issues.push({ field: latRaw === '' ? 'latitude' : 'longitude', code: 'COORD_PAIR' });
    } else {
      const lat = finiteInRange(latRaw, -90, 90);
      const lng = finiteInRange(lngRaw, -180, 180);
      if (lat === undefined) issues.push({ field: 'latitude', code: 'INVALID_COORD' });
      if (lng === undefined) issues.push({ field: 'longitude', code: 'INVALID_COORD' });
      if (lat !== undefined && lng !== undefined) center = { latitude: lat, longitude: lng };
    }
  }
  if (issues.length > 0) return err(issues);
  return ok({
    countryCode: cc,
    slug,
    name,
    timezone,
    ...(region !== '' ? { regionCode: region } : {}),
    ...(center !== undefined ? { center } : {}),
  });
}

/**
 * The states a market of `countryCode` can name (ADR-0124): the country's ACTIVE registered regions, by name. A market's `regionCode` is a
 * registered state of its OWN country (`400 MARKET_UNKNOWN_REGION` otherwise), so the form offers exactly these and never a typed code.
 */
export function marketRegionChoices(regions: readonly AdministrativeRegionView[], countryCode: string): readonly AdministrativeRegionView[] {
  const cc = countryCode.trim().toUpperCase();
  if (!CC_ALPHA2.test(cc)) return [];
  return regions.filter((r) => r.status === 'ACTIVE' && r.countryCode.toUpperCase() === cc).slice().sort((a, b) => a.name.localeCompare(b.name));
}

// ---- the regions console (ADR-0124): rename / retire a state ---------------------------------------

/** The longest state name the server accepts (the `administrative_regions_name` CHECK of m0225). */
export const REGION_NAME_MAX = 120;

function hasControlChar(text: string): boolean {
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return true;
  }
  return false;
}

/** A state name as the server will take it: trimmed, 1..120 characters, no control characters (line breaks, tabs); `undefined` when it will not. */
export function normalizeRegionName(raw: string): string | undefined {
  const name = raw.trim();
  if (name.length < 1 || name.length > REGION_NAME_MAX || hasControlChar(name)) return undefined;
  return name;
}

/** The countries that have at least one registered state (ACTIVE or retired - the console lists a retired one to reactivate it), by code. */
export function regionConsoleCountries(regions: readonly AdministrativeRegionView[]): readonly string[] {
  return [...new Set(regions.map((r) => r.countryCode.toUpperCase()))].sort();
}

/** Every state of one country, active and retired, by name - the rows of the console. */
export function regionConsoleRows(regions: readonly AdministrativeRegionView[], country: string): readonly AdministrativeRegionView[] {
  const cc = country.trim().toUpperCase();
  return regions.filter((r) => r.countryCode.toUpperCase() === cc).slice().sort((a, b) => a.name.localeCompare(b.name));
}

/** The result of a rename / retire / reactivate, applied to the catalog the screen holds (the server's answer replaces the row). */
export function applyRegionChange(regions: readonly AdministrativeRegionView[], changed: AdministrativeRegionView): readonly AdministrativeRegionView[] {
  return regions.map((r) => (r.code.toUpperCase() === changed.code.toUpperCase() ? changed : r));
}

/** A read that returned this many rows may have been cut by the server's page: the console then says it could not be checked in full, never a short count. */
export const REGION_IMPACT_BROADCAST_PAGE = 100;
export const REGION_IMPACT_BANNER_PAGE = 200;

/** How many of a state's campaigns are still to run: queued or sending (the list was read with `region=`, so these already reach the state). */
export function openBroadcastCount(campaigns: readonly BroadcastCampaignView[]): number {
  return campaigns.filter((c) => c.status === 'QUEUED' || c.status === 'RUNNING').length;
}

/** How many banners are live or scheduled for a state: switched on, not yet over, set for the state itself or for a city registered in it. */
export function stateBannerCount(banners: readonly BannerView[], regionCode: string, markets: readonly OperatingMarketView[], nowMs: number): number {
  const code = regionCode.toUpperCase();
  return banners.filter((b) => {
    if (!b.active || Date.parse(b.endsAt) <= nowMs) return false;
    if (b.scope.kind === 'REGION') return b.scope.region?.toUpperCase() === code;
    if (b.scope.kind === 'MARKET') return b.scope.marketId !== undefined && markets.some((m) => m.id === b.scope.marketId && m.regionCode?.toUpperCase() === code);
    return false;
  }).length;
}

// ---- runtime helpers (thin seams over the real operator market endpoints) -------------------------

interface MarketRuntime {
  readonly markets: MarketsApi;
}

/** Register a market (city) in the catalog; the server builds the typed `cc:slug` id. */
export function registerMarket(runtime: MarketRuntime, input: RegisterMarketInput): Promise<Result<{ registered: boolean; market: OperatingMarketView }, ApiError>> {
  return runtime.markets.registerMarket(input);
}

/** Read the market catalog (all, or one country's). */
export function listMarkets(runtime: MarketRuntime, country?: string): Promise<Result<{ country?: string; markets: readonly OperatingMarketView[] }, ApiError>> {
  return runtime.markets.listMarkets(country);
}

/** Change a market's lifecycle status (e.g. DRAFT → ACTIVE at launch) WITH its reason (D420: recorded with an audit row). */
export function setMarketStatus(runtime: MarketRuntime, id: string, status: MarketLaunchStatus, reason: MarketStatusReason): Promise<Result<{ market: OperatingMarketView; change: MarketStatusChangeView }, ApiError>> {
  return runtime.markets.setMarketStatus(id, status, reason);
}

/** D420: the recorded status changes of a market, newest first. */
export function marketStatusHistory(runtime: MarketRuntime, id: string): Promise<Result<{ marketId: string; changes: readonly MarketStatusChangeView[] }, ApiError>> {
  return runtime.markets.statusHistory(id);
}

/** ADR-0124: rename a state and/or retire / reactivate it (`market:manage`; audited). A retired state keeps what already names it and takes nothing new. */
export function updateRegion(
  runtime: MarketRuntime,
  code: string,
  change: { readonly name?: string; readonly status?: AdministrativeRegionView['status'] },
): Promise<Result<{ region: AdministrativeRegionView }, ApiError>> {
  return runtime.markets.updateRegion(code, change);
}
